import axios from "axios";

// ─────────────────────────────────────────────────────────────────────────────
// TOKEN STORAGE
// ─────────────────────────────────────────────────────────────────────────────

const tokenKeys = ["accessToken", "refreshToken", "tokenType", "user"];

const safeStorage = (type) => {
  try {
    return window[type];
  } catch {
    return null;
  }
};

/**
 * Find the storage layer that currently holds the user's tokens.
 * Preference: whichever storage has the refresh token. (Access tokens expire
 * and can be cleared independently; refresh token presence is the truer signal
 * of "this user chose 'remember me'".)
 */
const getActiveStorage = () => {
  const ls = safeStorage("localStorage");
  const ss = safeStorage("sessionStorage");
  if (ls?.getItem("refreshToken")) return ls;
  if (ss?.getItem("refreshToken")) return ss;
  return null;
};

/** True if the user logged in with "remember me" (tokens persisted to localStorage). */
const isRememberMe = () => getActiveStorage() === safeStorage("localStorage");

export const tokenStore = {
  setTokens: ({ accessToken, refreshToken, tokenType = "Bearer", user, rememberMe }) => {
    const ls = safeStorage("localStorage");
    const ss = safeStorage("sessionStorage");

    const primary = rememberMe ? ls : ss;
    const secondary = rememberMe ? ss : ls;

    if (primary) {
      if (accessToken) primary.setItem("accessToken", accessToken);
      if (refreshToken) primary.setItem("refreshToken", refreshToken);
      if (tokenType) primary.setItem("tokenType", tokenType);
      if (user) primary.setItem("user", JSON.stringify(user));
    }

    // Wipe the other storage so tokens never live in both places.
    if (secondary) {
      tokenKeys.forEach((key) => secondary.removeItem(key));
    }
  },

  clear: () => {
    [safeStorage("localStorage"), safeStorage("sessionStorage")].forEach((store) => {
      tokenKeys.forEach((key) => store?.removeItem(key));
    });
  },

  getAccessToken: () => getActiveStorage()?.getItem("accessToken") ?? null,
  getRefreshToken: () => getActiveStorage()?.getItem("refreshToken") ?? null,
  getUser: () => {
    const raw = getActiveStorage()?.getItem("user");
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// AXIOS CLIENT
// ─────────────────────────────────────────────────────────────────────────────

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "";
const authBaseUrl = (process.env.REACT_APP_AUTH_BASE_URL ?? "").replace(/\/+$/, "");

const axiosClient = axios.create({
  baseURL: apiBaseUrl,
});

// ─── REQUEST INTERCEPTOR ─────────────────────────────────────────────────────
// Attach the current access token to every outgoing request (if we have one).

axiosClient.interceptors.request.use((config) => {
  const token = tokenStore.getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─── RESPONSE INTERCEPTOR — AUTO-REFRESH ON 401 ──────────────────────────────
// Strategy:
//   - On 401, attempt to refresh the access token using the stored refresh token.
//   - Only ONE refresh runs at a time, even if multiple requests fail
//     simultaneously (single-flight via `refreshPromise`).
//   - If refresh fails: clear all tokens, redirect to /login, and reject every
//     queued request so callers stop waiting.
//   - Skip refresh entirely for paths that can't benefit from it (login,
//     refresh itself, logout) — refreshing during these would be wrong.

const redirectToLogin = () => {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/login") return;
  // Use replace so the broken page doesn't sit in history.
  window.location.replace("/login");
};

/** Paths where 401 should NOT trigger an auto-refresh. */
const shouldSkipRefresh = (config) => {
  const url = (config?.url ?? "").toString();
  // Match either relative ("/Auth/login") or absolute (with authBaseUrl prefix).
  return (
    url.includes("/Auth/login") ||
    url.includes("/Auth/refresh") ||
    url.includes("/Auth/logout")
  );
};

let refreshPromise = null;

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error || {};
    const status = response?.status;

    // Non-401 errors, or this request has already been retried once → bubble up.
    if (status !== 401 || !config || config._retry) {
      return Promise.reject(error);
    }

    // Paths that shouldn't trigger refresh — bubble up as-is.
    if (shouldSkipRefresh(config)) {
      return Promise.reject(error);
    }

    // No refresh token available → not logged in at all. Fail fast and clean.
    const refreshToken = tokenStore.getRefreshToken();
    if (!refreshToken) {
      tokenStore.clear();
      redirectToLogin();
      return Promise.reject(error);
    }

    // Mark this request so a future 401 won't trigger another refresh attempt
    // (prevents infinite loops if the refresh "succeeds" but the new access
    // token still gets rejected for some reason).
    config._retry = true;

    // Single-flight refresh: first caller starts the refresh, everyone else
    // awaits the same in-flight promise.
    if (!refreshPromise) {
      const rememberMe = isRememberMe();

      refreshPromise = axios
        .post(
          `${authBaseUrl}/Auth/refresh`,
          { refreshToken },
          { headers: { "Content-Type": "application/json" } }
        )
        .then(({ data }) => {
          // Persist the NEW refresh token. Backend rotates it on every call,
          // so the old one is now revoked. If we fail to store the new one,
          // the next refresh will trigger reuse detection and nuke all sessions.
          tokenStore.setTokens({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            tokenType: data.tokenType || "Bearer",
            rememberMe,
          });
          return data.accessToken;
        })
        .catch((refreshErr) => {
          // Refresh failed — either the token expired, was revoked, or reuse
          // was detected server-side. Either way, the session is dead.
          tokenStore.clear();
          redirectToLogin();
          // Re-throw so the original requests waiting on this promise
          // also reject (instead of hanging).
          throw refreshErr;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    let newToken;
    try {
      newToken = await refreshPromise;
    } catch (refreshError) {
      // Don't redirect again here — the refreshPromise's catch already did.
      return Promise.reject(refreshError);
    }

    // Refresh succeeded — replay the original request with the new token.
    if (newToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${newToken}`;
    }

    return axiosClient(config);
  }
);

export default axiosClient;